import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import ProgressIndicator from './ProgressIndicator';

// Helper functions for period-based streak calculation
// These are used by both the main component and the StreakCalendar

// Helper function to get period number for a given date
// Periods are numbered sequentially from a fixed reference point
const getPeriodNumber = (date, targetDays) => {
  // Use UTC to avoid timezone issues
  const dateUTC = new Date(Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate()
  ));
  
  // Use epoch (Jan 1, 1970) as reference for period calculation
  const epoch = new Date(Date.UTC(1970, 0, 1));
  const daysSinceEpoch = Math.floor((dateUTC - epoch) / (1000 * 60 * 60 * 24));
  const periodNumber = Math.floor(daysSinceEpoch / targetDays);
  
  return periodNumber;
};

// Helper function to get period start date from a period number
const getPeriodStartFromNumber = (periodNumber, targetDays) => {
  const epoch = new Date(Date.UTC(1970, 0, 1));
  const periodStart = new Date(epoch);
  periodStart.setUTCDate(epoch.getUTCDate() + (periodNumber * targetDays));
  return periodStart;
};

const CadenceTracker = ({ 
  friendId, 
  friendName, 
  contactHistory = [], 
  targetFrequency = 'weekly', // 'daily', 'weekly', 'biweekly', 'monthly'
  frequencyHistory = [] // Array of { frequency, changedAt } objects
}) => {
  const { t } = useTranslation();

  const formatFrequency = (frequency) => {
    const frequencyMap = {
      'daily': t('daily'),
      'weekly': t('weekly'),
      'biweekly': t('biweekly'),
      'monthly': t('monthly'),
      'quarterly': t('quarterly')
    };
    return frequencyMap[frequency] || frequency;
  };
  
  const getContactTypeLabel = (type) => {
    const typeMap = {
      'call': t('contactTypeCall'),
      'email': t('contactTypeEmail'),
      'text': t('contactTypeText'),
      'video': t('contactTypeVideo'),
      'meeting': t('contactTypeMeeting'),
      'other': t('contactTypeOther')
    };
    return typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1);
  };
  
  const [cadenceData, setCadenceData] = useState({
    currentStreak: 0,
    longestStreak: 0,
    averageGap: 0,
    consistencyScore: 0,
    lastContactDate: null,
    nextTargetDate: null
  });

  const [cadenceStatus, setCadenceStatus] = useState('good');

  // Calculate target days based on frequency
  const getTargetDays = (frequency) => {
    switch (frequency) {
      case 'daily': return 1;
      case 'weekly': return 7;
      case 'biweekly': return 14;
      case 'monthly': return 30;
      default: return 7;
    }
  };

  // Calculate cadence metrics
  useEffect(() => {
    console.log('CadenceTracker: contactHistory received:', contactHistory);
    console.log('CadenceTracker: contactHistory length:', contactHistory.length);
    console.log('CadenceTracker: friendId:', friendId, 'type:', typeof friendId);
    console.log('CadenceTracker: friendName:', friendName);
    console.log('CadenceTracker: frequencyHistory:', frequencyHistory);
    
    if (contactHistory.length === 0) {
      setCadenceData({
        currentStreak: 0,
        longestStreak: 0,
        averageGap: 0,
        consistencyScore: 0,
        lastContactDate: null,
        nextTargetDate: null
      });
      setCadenceStatus('needs_attention');
      return;
    }

    const sortedHistory = [...contactHistory].sort((a, b) => new Date(a.date) - new Date(b.date));
    const now = new Date();

    // Parse all contact dates to Date objects (using UTC for consistency)
    const parsedContacts = sortedHistory.map(contact => {
      let date;
      if (contact.date instanceof Date) {
        date = contact.date;
      } else if (contact.date && typeof contact.date === 'string') {
        if (contact.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = contact.date.split('-').map(Number);
          date = new Date(Date.UTC(year, month - 1, day));
        } else {
          date = new Date(contact.date);
        }
      } else {
        date = new Date(contact.date);
      }
      return { ...contact, parsedDate: date };
    });

    // Helper function to calculate streak for a given frequency and date range
    const calculateStreakForFrequency = (contacts, frequency, startDate = null, endDate = null) => {
      const targetDays = getTargetDays(frequency);
      
      // Filter contacts by date range if provided
      const filteredContacts = contacts.filter(contact => {
        if (startDate && contact.parsedDate < startDate) return false;
        if (endDate && contact.parsedDate >= endDate) return false;
        return true;
      });
      
      if (filteredContacts.length === 0) return { currentStreak: 0, longestStreak: 0 };
      
      // Group contacts by period number
      const contactsByPeriod = new Map();
      filteredContacts.forEach(contact => {
        const periodNumber = getPeriodNumber(contact.parsedDate, targetDays);
        if (!contactsByPeriod.has(periodNumber)) {
          contactsByPeriod.set(periodNumber, []);
        }
        contactsByPeriod.get(periodNumber).push(contact);
      });

      // Get all period numbers that have at least one contact, sorted chronologically
      const periodsWithContacts = Array.from(contactsByPeriod.keys())
        .sort((a, b) => a - b)
        .map(periodNum => ({
          periodNumber: periodNum,
          start: getPeriodStartFromNumber(periodNum, targetDays),
          contacts: contactsByPeriod.get(periodNum)
        }));

      // Calculate current streak: count consecutive periods from most recent period backwards
      let currentStreak = 0;
      const checkEndDate = endDate || now;
      const checkPeriodNumber = getPeriodNumber(checkEndDate, targetDays);
      
      // Check if current period has a contact
      const currentPeriodHasContact = contactsByPeriod.has(checkPeriodNumber);
      
      // Start checking from the current period (or previous if current has no contact)
      let checkPeriodNum = currentPeriodHasContact ? checkPeriodNumber : checkPeriodNumber - 1;
      
      // Work backwards through periods
      while (contactsByPeriod.has(checkPeriodNum)) {
        currentStreak++;
        checkPeriodNum--;
        
        // Safety limit to prevent infinite loops
        if (currentStreak > 1000) break;
      }

      // Calculate longest streak: find longest sequence of consecutive periods with contacts
      let longestStreak = 0;
      let tempStreak = 0;
      let lastPeriodNumber = null;
      
      periodsWithContacts.forEach(period => {
        if (lastPeriodNumber === null) {
          // First period
          tempStreak = 1;
          lastPeriodNumber = period.periodNumber;
        } else {
          // Check if this period is consecutive to the last one
          if (period.periodNumber === lastPeriodNumber + 1) {
            // Consecutive period
            tempStreak++;
          } else {
            // Streak broken, start new streak
            longestStreak = Math.max(longestStreak, tempStreak);
            tempStreak = 1;
          }
          lastPeriodNumber = period.periodNumber;
        }
      });
      longestStreak = Math.max(longestStreak, tempStreak);
      
      return { currentStreak, longestStreak };
    };

    // If there's no frequency history, calculate normally (backward compatible)
    if (!frequencyHistory || frequencyHistory.length === 0) {
      const targetDays = getTargetDays(targetFrequency);
      const streakResult = calculateStreakForFrequency(parsedContacts, targetFrequency);
      const currentStreak = streakResult.currentStreak;
      const longestStreak = streakResult.longestStreak;
      
      // Calculate average gap
      let totalGap = 0;
      let gapCount = 0;
      for (let i = 1; i < parsedContacts.length; i++) {
        const prevDate = parsedContacts[i - 1].parsedDate;
        const currDate = parsedContacts[i].parsedDate;
        const daysDiff = (currDate - prevDate) / (1000 * 60 * 60 * 24);
        totalGap += daysDiff;
        gapCount++;
      }
      const averageGap = gapCount > 0 ? totalGap / gapCount : 0;

      // Determine last contact date and next target date
      const lastContactDate = parsedContacts.length > 0 
        ? parsedContacts[parsedContacts.length - 1].parsedDate 
        : null;
      
      // Next target is simply last contact date + target days (not based on period boundaries)
      const nextTargetDate = lastContactDate ? (() => {
        const nextDate = new Date(lastContactDate);
        // Add target days in milliseconds to avoid timezone/date boundary issues
        nextDate.setTime(nextDate.getTime() + (targetDays * 24 * 60 * 60 * 1000));
        return nextDate;
      })() : null;

      setCadenceData({
        currentStreak,
        longestStreak,
        averageGap: Math.round(averageGap),
        consistencyScore: 0,
        lastContactDate,
        nextTargetDate
      });

      // Determine cadence status
      let status = 'needs_attention';
      if (parsedContacts.length > 0) {
        const lastContactDate = parsedContacts[parsedContacts.length - 1].parsedDate;
        const daysSinceLastContact = Math.floor((now - lastContactDate) / (1000 * 60 * 60 * 24));
        const targetDays = getTargetDays(targetFrequency);
        
        if (daysSinceLastContact <= targetDays) {
          if (currentStreak >= 4) {
            status = 'excellent';
          } else if (currentStreak >= 2) {
            status = 'good';
          } else {
            status = 'needs_improvement';
          }
        } else if (daysSinceLastContact <= targetDays * 1.5) {
          status = 'needs_improvement';
        } else {
          status = 'needs_attention';
        }
      }
      setCadenceStatus(status);
      return;
    }

    // Handle frequency history: calculate streaks for each frequency period
    // Sort frequency history chronologically
    const sortedFrequencyHistory = [...frequencyHistory].sort((a, b) => 
      new Date(a.changedAt) - new Date(b.changedAt)
    );

    // Calculate historical streaks (before current frequency)
    let totalHistoricalStreak = 0;
    let historicalLongestStreak = 0;
    
    // Process each frequency period
    let periodStartDate = null; // Start from the beginning
    for (let i = 0; i < sortedFrequencyHistory.length; i++) {
      const historyEntry = sortedFrequencyHistory[i];
      const periodEndDate = new Date(historyEntry.changedAt);
      const frequency = historyEntry.frequency;
      
      // Calculate streak for this frequency period
      const streakResult = calculateStreakForFrequency(
        parsedContacts, 
        frequency, 
        periodStartDate, 
        periodEndDate
      );
      
      // Add to total historical streak (only if it's a complete streak)
      // We'll add the longest streak from this period to historical longest
      historicalLongestStreak = Math.max(historicalLongestStreak, streakResult.longestStreak);
      
      // For total streak, we need to check if the streak continues into the next period
      // For now, we'll sum up the longest streaks from each period
      // But actually, we want to preserve the streak count at the time of change
      // So we should use the currentStreak at the time of change
      totalHistoricalStreak += streakResult.currentStreak;
      
      periodStartDate = periodEndDate;
    }

    // Calculate current frequency streak (from last frequency change to now)
    const lastFrequencyChange = sortedFrequencyHistory[sortedFrequencyHistory.length - 1];
    const currentFrequencyStartDate = new Date(lastFrequencyChange.changedAt);
    const currentStreakResult = calculateStreakForFrequency(
      parsedContacts,
      targetFrequency,
      currentFrequencyStartDate,
      null // No end date, go to now
    );
    
    // Total streak = historical + current
    const totalCurrentStreak = totalHistoricalStreak + currentStreakResult.currentStreak;
    const totalLongestStreak = Math.max(historicalLongestStreak, currentStreakResult.longestStreak);

    // Calculate average gap
    let totalGap = 0;
    let gapCount = 0;
    for (let i = 1; i < parsedContacts.length; i++) {
      const prevDate = parsedContacts[i - 1].parsedDate;
      const currDate = parsedContacts[i].parsedDate;
      const daysDiff = (currDate - prevDate) / (1000 * 60 * 60 * 24);
      totalGap += daysDiff;
      gapCount++;
    }
    const averageGap = gapCount > 0 ? totalGap / gapCount : 0;

    // Determine last contact date and next target date
    const lastContactDate = parsedContacts.length > 0 
      ? parsedContacts[parsedContacts.length - 1].parsedDate 
      : null;
    
    const targetDays = getTargetDays(targetFrequency);
    // Next target is simply last contact date + target days (not based on period boundaries)
    const nextTargetDate = lastContactDate ? (() => {
      const nextDate = new Date(lastContactDate);
      // Add target days in milliseconds to avoid timezone/date boundary issues
      nextDate.setTime(nextDate.getTime() + (targetDays * 24 * 60 * 60 * 1000));
      return nextDate;
    })() : null;

    setCadenceData({
      currentStreak: totalCurrentStreak, // Total including historical
      longestStreak: totalLongestStreak,
      averageGap: Math.round(averageGap),
      consistencyScore: 0,
      lastContactDate,
      nextTargetDate
    });

    // Determine cadence status based on current frequency streak
    let status = 'needs_attention';
    if (parsedContacts.length > 0) {
      const lastContactDate = parsedContacts[parsedContacts.length - 1].parsedDate;
      const daysSinceLastContact = Math.floor((now - lastContactDate) / (1000 * 60 * 60 * 24));
      
      if (daysSinceLastContact <= targetDays) {
        // Use current frequency streak for status (not total)
        if (currentStreakResult.currentStreak >= 4) {
          status = 'excellent';
        } else if (currentStreakResult.currentStreak >= 2) {
          status = 'good';
        } else {
          status = 'needs_improvement';
        }
      } else if (daysSinceLastContact <= targetDays * 1.5) {
        status = 'needs_improvement';
      } else {
        status = 'needs_attention';
      }
    }
    
    setCadenceStatus(status);
  }, [contactHistory, targetFrequency, frequencyHistory]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'excellent': return '#10B981';
      case 'good': return '#22C55E';
      case 'needs_improvement': return '#F59E0B';
      case 'needs_attention': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const getStatusEmoji = (status) => {
    switch (status) {
      case 'excellent': return '🔥';
      case 'good': return '👍';
      case 'needs_improvement': return '⚠️';
      case 'needs_attention': return '🚨';
      default: return '📊';
    }
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case 'excellent': return t('excellentCadence');
      case 'good': return t('goodConsistency');
      case 'needs_improvement': return t('couldBeMoreConsistent');
      case 'needs_attention': return t('needsAttentionCadence');
      default: return t('noDataYet');
    }
  };

  const getDaysUntilNextTarget = () => {
    if (!cadenceData.nextTargetDate) return null;
    const now = new Date();
    const daysDiff = (cadenceData.nextTargetDate - now) / (1000 * 60 * 60 * 24);
    return Math.ceil(daysDiff);
  };

  const daysUntilNext = getDaysUntilNextTarget();
  const isOverdue = daysUntilNext < 0;

  // Streak Calendar Component
  const StreakCalendar = ({ contactHistory, targetFrequency, currentStreak, longestStreak, getContactTypeLabel }) => {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const { t } = useTranslation();

    const getContactTypeLabelLocal = (type) => {
      const typeMap = {
        'call': t('contactTypeCall'),
        'email': t('contactTypeEmail'),
        'text': t('contactTypeText'),
        'video': t('contactTypeVideo'),
        'meeting': t('contactTypeMeeting'),
        'other': t('contactTypeOther')
      };
      return typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1);
    };

    const formatFrequency = (frequency) => {
      const frequencyMap = {
        'daily': t('daily'),
        'weekly': t('weekly'),
        'biweekly': t('biweekly'),
        'monthly': t('monthly'),
        'quarterly': t('quarterly')
      };
      return frequencyMap[frequency] || frequency;
    };

    // Get target days for frequency
    const getTargetDays = (frequency) => {
      switch (frequency) {
        case 'daily': return 1;
        case 'weekly': return 7;
        case 'biweekly': return 14;
        case 'monthly': return 30;
        case 'quarterly': return 90;
        default: return 7;
      }
    };

    // Group contacts by date (using UTC to avoid timezone issues)
    const contactsByDate = useMemo(() => {
      const grouped = {};
      contactHistory.forEach(contact => {
        let date;
        // Handle different date formats
        if (contact.date instanceof Date) {
          date = contact.date;
        } else if (contact.date && typeof contact.date === 'string') {
          // If it's already a date string (YYYY-MM-DD), use it directly
          if (contact.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const [year, month, day] = contact.date.split('-').map(Number);
            date = new Date(Date.UTC(year, month - 1, day));
          } else {
            // Parse ISO string and use UTC date components
            date = new Date(contact.date);
          }
        } else {
          date = new Date(contact.date);
        }
        
        // Use UTC date components to avoid timezone shifts
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        const dateKey = `${year}-${month}-${day}`;
        
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(contact);
      });
      return grouped;
    }, [contactHistory]);

    // Calculate period streaks (using target frequency, not hardcoded weeks)
    const periodStreaks = useMemo(() => {
      const targetDays = getTargetDays(targetFrequency);
      const sortedHistory = [...contactHistory].sort((a, b) => new Date(a.date) - new Date(b.date));
      const periods = [];
      const now = new Date();
      
      // Get last 12 periods based on target frequency
      for (let i = 11; i >= 0; i--) {
        const periodStart = new Date(now);
        periodStart.setUTCDate(now.getUTCDate() - (i * targetDays));
        periodStart.setUTCHours(0, 0, 0, 0);
        
        const periodEnd = new Date(periodStart);
        periodEnd.setUTCDate(periodStart.getUTCDate() + targetDays - 1);
        periodEnd.setUTCHours(23, 59, 59, 999);
        
        // Check if there's at least one contact in this period
        const hasContact = sortedHistory.some(contact => {
          let contactDate;
          if (contact.date instanceof Date) {
            contactDate = contact.date;
          } else if (contact.date && typeof contact.date === 'string') {
            if (contact.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
              const [year, month, day] = contact.date.split('-').map(Number);
              contactDate = new Date(Date.UTC(year, month - 1, day));
            } else {
              contactDate = new Date(contact.date);
            }
          } else {
            contactDate = new Date(contact.date);
          }
          return contactDate >= periodStart && contactDate <= periodEnd;
        });
        
        periods.push({
          start: periodStart,
          end: periodEnd,
          hasContact
        });
      }
      
      return periods;
    }, [contactHistory, targetFrequency]);

    // Calculate days until streak breaks
    const getDaysUntilStreakBreaks = () => {
      if (contactHistory.length === 0) return null;
      
      const sortedHistory = [...contactHistory].sort((a, b) => new Date(b.date) - new Date(a.date));
      const lastContact = new Date(sortedHistory[0].date);
      const now = new Date();
      
      // Use UTC to avoid timezone issues
      const lastContactUTC = Date.UTC(
        lastContact.getUTCFullYear(),
        lastContact.getUTCMonth(),
        lastContact.getUTCDate()
      );
      const nowUTC = Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate()
      );
      
      const daysSinceLastContact = Math.floor((nowUTC - lastContactUTC) / (1000 * 60 * 60 * 24));
      const targetDays = getTargetDays(targetFrequency);
      const daysRemaining = targetDays - daysSinceLastContact;
      
      return daysRemaining > 0 ? daysRemaining : 0;
    };

    const daysUntilStreakBreaks = getDaysUntilStreakBreaks();
    const streakEndDate = contactHistory.length > 0 ? (() => {
      const sortedHistory = [...contactHistory].sort((a, b) => new Date(b.date) - new Date(a.date));
      let lastContact;
      if (sortedHistory[0].date instanceof Date) {
        lastContact = sortedHistory[0].date;
      } else if (sortedHistory[0].date && typeof sortedHistory[0].date === 'string') {
        if (sortedHistory[0].date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = sortedHistory[0].date.split('-').map(Number);
          lastContact = new Date(Date.UTC(year, month - 1, day));
        } else {
          lastContact = new Date(sortedHistory[0].date);
        }
      } else {
        lastContact = new Date(sortedHistory[0].date);
      }
      
      const targetDays = getTargetDays(targetFrequency);
      // Get the period number of the last contact, then get the start of the next period
      const lastContactPeriodNumber = getPeriodNumber(lastContact, targetDays);
      const nextPeriodNumber = lastContactPeriodNumber + 1;
      const nextPeriodStart = getPeriodStartFromNumber(nextPeriodNumber, targetDays);
      // The streak ends at the end of the next period (start of period after that)
      const endDate = new Date(nextPeriodStart);
      endDate.setUTCDate(endDate.getUTCDate() + targetDays - 1);
      endDate.setUTCHours(23, 59, 59, 999);
      return endDate;
    })() : null;

    // Get calendar days for current month
    const getCalendarDays = () => {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const daysInMonth = lastDay.getDate();
      const startingDayOfWeek = firstDay.getDay();
      
      const days = [];
      
      // Add previous month's trailing days
      const prevMonthLastDay = new Date(year, month, 0).getDate();
      for (let i = startingDayOfWeek - 1; i >= 0; i--) {
        days.push({
          date: new Date(year, month - 1, prevMonthLastDay - i),
          isCurrentMonth: false,
          contacts: []
        });
      }
      
      // Add current month's days
      const today = new Date();
      const todayYear = today.getUTCFullYear();
      const todayMonth = today.getUTCMonth();
      const todayDay = today.getUTCDate();
      
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(Date.UTC(year, month, day));
        // Create date key using UTC to match the grouping
        const yearUTC = date.getUTCFullYear();
        const monthUTC = String(date.getUTCMonth() + 1).padStart(2, '0');
        const dayUTC = String(date.getUTCDate()).padStart(2, '0');
        const dateKey = `${yearUTC}-${monthUTC}-${dayUTC}`;
        
        // Check if it's today using UTC
        const isToday = yearUTC === todayYear && 
                       date.getUTCMonth() === todayMonth && 
                       dayUTC === String(todayDay).padStart(2, '0');
        
        days.push({
          date,
          isCurrentMonth: true,
          contacts: contactsByDate[dateKey] || [],
          isToday,
          isFuture: date > new Date()
        });
      }
      
      // Add next month's leading days to fill the grid
      const remainingDays = 42 - days.length; // 6 weeks * 7 days
      for (let day = 1; day <= remainingDays; day++) {
        days.push({
          date: new Date(year, month + 1, day),
          isCurrentMonth: false,
          contacts: []
        });
      }
      
      return days;
    };

    const calendarDays = getCalendarDays();
    const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    const navigateMonth = (direction) => {
      setCurrentMonth(prev => {
        const newDate = new Date(prev);
        newDate.setMonth(prev.getMonth() + direction);
        return newDate;
      });
    };

    const getContactIcon = (type) => {
      const icons = {
        'call': '📞',
        'email': '📧',
        'text': '💬',
        'video': '📹',
        'meeting': '🤝',
        'other': '💭'
      };
      return icons[type] || '💭';
    };

    const getIntensityColor = (count) => {
      if (count === 0) return '#E5E7EB';
      if (count === 1) return '#93C5FD';
      if (count <= 3) return '#60A5FA';
      if (count <= 5) return '#3B82F6';
      return '#1D4ED8';
    };

    return (
      <div className="streak-calendar">
        <div className="streak-header">
          <div className="streak-metrics">
            <div className="streak-metric">
              <div className="streak-label">{t('yourStreak')}</div>
              <div className="streak-value">
                <span className="streak-number">{currentStreak}</span>
                <span className="streak-unit">{t('weeksUnit')}</span>
              </div>
            </div>
            <div className="streak-metric">
              <div className="streak-label">{t('longestStreakEver')}</div>
              <div className="streak-value">
                <span className="streak-number">{longestStreak}</span>
                <span className="streak-unit">{t('weeksUnit')}</span>
              </div>
            </div>
            {daysUntilStreakBreaks !== null && daysUntilStreakBreaks >= 0 && (
              <div className="streak-metric streak-countdown">
                <div className="streak-label">{t('daysUntilStreakBreaks')}</div>
                <div className="streak-value">
                  <span className={`streak-number ${daysUntilStreakBreaks <= 2 ? 'urgent' : daysUntilStreakBreaks <= 5 ? 'warning' : ''}`}>
                    {daysUntilStreakBreaks}
                  </span>
                  <span className="streak-unit">{t('days')}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="calendar-container">
          <div className="calendar-header">
            <button className="calendar-nav-btn" onClick={() => navigateMonth(-1)}>←</button>
            <h5 className="calendar-month">{monthName}</h5>
            <button className="calendar-nav-btn" onClick={() => navigateMonth(1)}>→</button>
          </div>

          <div className="calendar-grid-wrapper">
            <div className="calendar-grid">
              <div className="calendar-weekdays">
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, idx) => (
                  <div key={idx} className="weekday-label">{day}</div>
                ))}
              </div>
              
              <div className="calendar-days">
                {calendarDays.map((day, idx) => {
                  const contactCount = day.contacts.length;
                  const hasContact = contactCount > 0;
                  const isPast = day.date < new Date() && !day.isToday;
                  const isToday = day.isToday;
                  
                  // Check if this day is within the streak window (days until streak breaks)
                  const isInStreakWindow = streakEndDate && day.isCurrentMonth && !day.isFuture && (() => {
                    const dayUTC = Date.UTC(
                      day.date.getUTCFullYear(),
                      day.date.getUTCMonth(),
                      day.date.getUTCDate()
                    );
                    const endDateUTC = Date.UTC(
                      streakEndDate.getUTCFullYear(),
                      streakEndDate.getUTCMonth(),
                      streakEndDate.getUTCDate()
                    );
                    const nowUTC = Date.UTC(
                      new Date().getUTCFullYear(),
                      new Date().getUTCMonth(),
                      new Date().getUTCDate()
                    );
                    return dayUTC >= nowUTC && dayUTC <= endDateUTC;
                  })();
                  
                  // Check if this is a critical day (last day before streak breaks)
                  const isCriticalDay = isInStreakWindow && streakEndDate && (() => {
                    const dayUTC = Date.UTC(
                      day.date.getUTCFullYear(),
                      day.date.getUTCMonth(),
                      day.date.getUTCDate()
                    );
                    const endDateUTC = Date.UTC(
                      streakEndDate.getUTCFullYear(),
                      streakEndDate.getUTCMonth(),
                      streakEndDate.getUTCDate()
                    );
                    return dayUTC === endDateUTC;
                  })();
                  
                  // Get the most common contact type for the day
                  const getPrimaryContactType = () => {
                    if (day.contacts.length === 0) return null;
                    const typeCounts = {};
                    day.contacts.forEach(contact => {
                      typeCounts[contact.type] = (typeCounts[contact.type] || 0) + 1;
                    });
                    return Object.keys(typeCounts).reduce((a, b) => typeCounts[a] > typeCounts[b] ? a : b);
                  };
                  
                  const primaryType = getPrimaryContactType();
                  
                  return (
                    <div
                      key={idx}
                      className={`calendar-day ${!day.isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''} ${day.isFuture ? 'future' : ''} ${hasContact ? 'has-contact' : ''} ${isInStreakWindow ? 'streak-window' : ''} ${isCriticalDay ? 'critical-day' : ''}`}
                      title={day.isCurrentMonth && hasContact ? `${day.date.toLocaleDateString()}: ${contactCount} ${contactCount === 1 ? t('interaction') : t('interactions')}${primaryType ? ` (${getContactTypeLabelLocal(primaryType)})` : ''}` : day.isCurrentMonth ? day.date.toLocaleDateString() : ''}
                    >
                      {day.isCurrentMonth && (
                        <>
                          <div className={`day-number ${hasContact ? 'has-contact-number' : ''} ${isCriticalDay ? 'critical-number' : ''}`}>
                            {day.date.getDate()}
                          </div>
                          {isCriticalDay && !hasContact && (
                            <div className="critical-indicator" title={t('lastDayToMaintainStreak')}>
                              ⚠️
                            </div>
                          )}
                          {hasContact && (
                            <div 
                              className="day-activity"
                              style={{ 
                                backgroundColor: getIntensityColor(contactCount),
                              }}
                            >
                              {primaryType && (
                                <span className="activity-icon">{getContactIcon(primaryType)}</span>
                              )}
                              {contactCount > 1 && (
                                <span className="activity-count">{contactCount}</span>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="streak-sidebar">
              {periodStreaks.map((period, idx) => (
                <div 
                  key={idx} 
                  className={`week-streak-indicator ${period.hasContact ? 'active' : ''}`}
                  title={period.hasContact ? t('periodStreakMet') : t('periodStreakMissed')}
                >
                  {period.hasContact && <span className="checkmark">✓</span>}
                </div>
              ))}
              <div className="total-streak-flame" title={t('totalStreakPeriods', { count: currentStreak })}>
                <span className="flame-icon">🔥</span>
                <span className="flame-number">{currentStreak}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="cadence-tracker">
      <div className="cadence-header">
        <h4>{t('interactionCadence')}</h4>
        <div className="cadence-status">
          <span className={`status-indicator ${cadenceStatus}`}>
            {getStatusEmoji(cadenceStatus)} {getStatusMessage(cadenceStatus)}
          </span>
        </div>
      </div>

      <div className="cadence-overview">
        <div className="cadence-description">
          {t('cadenceDescription', { name: friendName, frequency: formatFrequency(targetFrequency) })}
        </div>

        <div className="cadence-stats">
          <div className="stat-item">
            <div className="stat-value">{cadenceData.currentStreak}</div>
            <div className="stat-label">{t('currentStreak')}</div>
            <div className="stat-hint">
              {t('streakHint')}
            </div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{cadenceData.longestStreak}</div>
            <div className="stat-label">{t('bestStreak')}</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{cadenceData.averageGap}d</div>
            <div className="stat-label">{t('avgGap')}</div>
          </div>
        </div>
      </div>

      <div className="cadence-details">
        <div className="target-info">
          <h5>{t('targetContact', { frequency: formatFrequency(targetFrequency) })}</h5>
          <p className="target-description">
            {t('streakExplanation', { frequency: formatFrequency(targetFrequency) })}
          </p>
          <div className="target-details">
            <div className="target-item">
              <span className="target-label">{t('lastContact')}</span>
              <span className="target-value">
                {cadenceData.lastContactDate 
                  ? cadenceData.lastContactDate.toLocaleDateString()
                  : t('never')
                }
              </span>
            </div>
            <div className="target-item">
              <span className="target-label">{t('nextTarget')}</span>
              <span className={`target-value ${isOverdue ? 'overdue' : ''}`}>
                {cadenceData.nextTargetDate 
                  ? cadenceData.nextTargetDate.toLocaleDateString()
                  : t('notSet')
                }
                {daysUntilNext !== null && (
                  <span className="days-indicator">
                    ({isOverdue ? t('daysOverdue', { count: Math.abs(daysUntilNext) }) : t('daysLeft', { count: daysUntilNext })})
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>

        <div className="cadence-tips">
          <h5>{t('tipsForBetterCadence')}</h5>
          <div className="tips-list">
            {cadenceStatus === 'excellent' && (
              <div className="tip-item success">
                <span className="tip-icon">🎉</span>
                <span>{t('doingAmazing', { name: friendName })}</span>
              </div>
            )}
            {cadenceStatus === 'good' && (
              <div className="tip-item">
                <span className="tip-icon">💡</span>
                <span>{t('greatJob', { name: friendName })}</span>
              </div>
            )}
            {cadenceStatus === 'needs_improvement' && (
              <div className="tip-item">
                <span className="tip-icon">📅</span>
                <span>{t('setReminder', { name: friendName, frequency: targetFrequency })}</span>
              </div>
            )}
            {cadenceStatus === 'needs_attention' && (
              <div className="tip-item urgent">
                <span className="tip-icon">🚨</span>
                <span>{t('beenAWhile', { name: friendName })}</span>
              </div>
            )}
            {contactHistory.length === 0 && (
              <div className="tip-item">
                <span className="tip-icon">🌱</span>
                <span>{t('startFriendshipJourney', { name: friendName })}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Streak Calendar View */}
      <StreakCalendar 
        contactHistory={contactHistory}
        targetFrequency={targetFrequency}
        currentStreak={cadenceData.currentStreak}
        longestStreak={cadenceData.longestStreak}
        getContactTypeLabel={getContactTypeLabel}
      />
    </div>
  );
};

export default CadenceTracker;
